import { Duration } from 'luxon';
import { Instance, types, flow } from 'mobx-state-tree';
import getAppRoot from '../utils/getAppRoot';
import InsightsQuery from '../queries/organization/InsightsQuery';
import {
  CompletedRoutesInsightsDtoModel,
  CompletedRoutesInsightsDtoModelType,
  DistanceUnit,
  CompletedRoutesInsightsOverTimeDtoModelType,
  CompletedRoutesInsightsOverTimeDtoModel,
  InsightsInterval,
  InsightsDriverBreakdownSortingDirection,
  CompletedRoutesInsightsDriverBreakdownOvertimePaginatedDtoModelType,
  CompletedRoutesInsightsDriverBreakdownOvertimePaginatedDtoModel,
  DriverDtoModelType,
} from '../models';
import formatNumber from '../../../helpers/formatNumber';
import getHrsMinString from '../../../helpers/getHrsMinString';
import getFormattedDistance from '../../../helpers/getFormattedDistance';
import getKmsOrMiles from '../../../helpers/getKmsOrMiles';
import InsightsQueryOverTime from '../queries/organization/InsightsQueryOverTime';
import getDataOverTimeInterval from './utils/getDataOverTimeInterval';
import InsightsDateRange from './models/InsightsDateRange';
import { getInsightsDateRange, updateInsightsDateRangeInLocalStorage } from './utils/insightsDateRangeUtils';
import { BaseInsightsQueryParams, InsightsChartType, InsightsDetailsQueryParams } from '../Router/constants';
import { getMetricQuery } from './utils/insightsMetrics';
import { getSelectedMetric } from './utils/insightsDetailsUtils';
import { SELECTED_FILTERED_DRIVER_IDS_LOCAL_STORAGE_KEY, SELECTED_FILTERED_DIMENSION_LOCAL_STORAGE_KEY } from '../../../components/insights/Filter';
import { FilterDimension, Metric } from './constants';
import { captureDriverCountWarning } from './utils/sentryUtils';

const Insights = types
  .model('Insights')
  .props({
    routeInsights: types.maybeNull(CompletedRoutesInsightsDtoModel),
    lastUpdatedTimestamp: types.maybeNull(types.string),
    completedRoutesInsightOverTimeQueryParams: types.maybeNull(types.model('CompletedRoutesInsightOverTimeQueryParams', {
      startDate: types.Date,
      endDate: types.Date,
      interval: types.enumeration<InsightsInterval>('InsightsInterval', Object.values(InsightsInterval)),
    })),
    completedRouteInsightsOvertime: types.maybeNull(CompletedRoutesInsightsOverTimeDtoModel),
    completedRoutesInsightsDriverBreakdownOvertimePaginated: types.maybeNull(CompletedRoutesInsightsDriverBreakdownOvertimePaginatedDtoModel),
    isLoadingDriverBreakdown: types.optional(types.boolean, false),
    showFilterOptions: types.optional(types.boolean, false),
    selectedFilterDimensions: types.optional(types.array(types.enumeration<FilterDimension>('FilterDimension', Object.values(FilterDimension))), []),
    selectedDrivers: types.array(types.string),
  })
  .views((self) => ({
    /*
     * TODO: Technical Debt - https://github.com/routific/planning-service/issues/2201
     * Ideally, we want to use Driver store as the central source of information.
     * However, we'll probably change that structure and the current `drivers` query doesn't support
     * deleted drivers.
     * In the future, we'll paginate that query and update it accordingly.
     * When that bright day comes, we can refactor this view to reference the central drivers' store.
     */
    get drivers(): Array<DriverDtoModelType> {
      return self.completedRoutesInsightsDriverBreakdownOvertimePaginated?.edges?.map((edge) => ({
        id: String(edge.node.driverId),
        name: edge.node.driverName,
        deleted: edge.node.deleted,
      }) as DriverDtoModelType) || [];
    },
    get filteredDriverBreakdownInsights() {
      if (self.selectedDrivers.length) {
        return self.completedRoutesInsightsDriverBreakdownOvertimePaginated?.edges
          ?.filter(edge => self.selectedDrivers.includes(String(edge.node.driverId)))
          .map(edge => edge.node);
      }

      return self.completedRoutesInsightsDriverBreakdownOvertimePaginated?.edges?.map(edge => edge.node);
    },
    get completedRoutesInsights() {
      return self.routeInsights;
    },
    get insightsDateRange(): InsightsDateRange {
      return getInsightsDateRange(getAppRoot(self).router.queryParams);
    },
    get selectedMetric(): Metric | undefined {
      return getSelectedMetric(getAppRoot(self).router.queryParams);
    },
    get selectedFilterDimensionsCount(): number {
      return self.selectedFilterDimensions.length;
    },
    get numOfRoutes() {
      return formatNumber(self.routeInsights?.completedRoutes || 0);
    },
    get timeDriven() {
      return getHrsMinString(Duration.fromObject({ seconds: self.routeInsights?.completedWorkingTime || 0 }).as('minutes'));
    },
    get distanceDriven() {
      const distanceUnit =
        (getAppRoot(self).organization.settings.distanceUnit as DistanceUnit) ||
        DistanceUnit.KM;
      const formattedDistanceNum = getFormattedDistance(
        self.routeInsights?.estimatedDistanceCompleted,
        distanceUnit,
        2,
      ) || 0;
      return getKmsOrMiles(formattedDistanceNum, distanceUnit);
    },
    get completedOrders() {
      return formatNumber(self.routeInsights?.completedOrders || 0);
    },
    get lastUpdatedTime() {
      return self.lastUpdatedTimestamp;
    },
    get isExportDisabled() {
      return self.isLoadingDriverBreakdown;
    },
    get currentChartType() {
      return getAppRoot(self).router.queryParams.get(BaseInsightsQueryParams.CHART_TYPE) ?? InsightsChartType.BAR;
    },
  }))
  .actions((self) => ({
    setShowFilterOptions(value: boolean) {
      self.showFilterOptions = value;
    },
    setRouteInsights(data: CompletedRoutesInsightsDtoModelType) {
      self.routeInsights = data;
    },
    setLastUpdatedTimestamp(timestamp: string) {
      self.lastUpdatedTimestamp = timestamp;
    },
    setInsightsDateRange(dateRange: InsightsDateRange) {
      updateInsightsDateRangeInLocalStorage(dateRange);
      getAppRoot(self).router.replaceQueryParams({
        [BaseInsightsQueryParams.START_DATE]: dateRange.startDate,
        [BaseInsightsQueryParams.END_DATE]: dateRange.endDate,
      });
    },
    setSelectedMetric(metric: Metric) {
      getAppRoot(self).router.replaceQueryParams({
        [InsightsDetailsQueryParams.METRIC]: metric,
      });
    },
    setSelectedDrivers(driverIds: string[]) {
      self.selectedFilterDimensions.replace([FilterDimension.DRIVERS]);
      self.selectedDrivers.replace(driverIds);
      localStorage.setItem(SELECTED_FILTERED_DIMENSION_LOCAL_STORAGE_KEY, JSON.stringify([FilterDimension.DRIVERS]));
      localStorage.setItem(SELECTED_FILTERED_DRIVER_IDS_LOCAL_STORAGE_KEY, JSON.stringify(driverIds));
    },
    resetSelectedFilterDimensions() {
      self.selectedDrivers.replace([]);
      self.selectedFilterDimensions.replace([]);
      localStorage.removeItem(SELECTED_FILTERED_DRIVER_IDS_LOCAL_STORAGE_KEY);
      localStorage.removeItem(SELECTED_FILTERED_DIMENSION_LOCAL_STORAGE_KEY);
    },
    setCompletedRoutesInsightOverTimeQueryParams(startDate: string, endDate: string) {
      self.completedRoutesInsightOverTimeQueryParams = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        interval: getDataOverTimeInterval(startDate, endDate),
      };
    },
    setCompletedRouteInsightsOverTime(data: CompletedRoutesInsightsOverTimeDtoModelType) {
      self.completedRouteInsightsOvertime = data;
    },
    setCompletedRoutesInsightsDriverBreakdownOvertimePaginated(data: CompletedRoutesInsightsDriverBreakdownOvertimePaginatedDtoModelType) {
      self.completedRoutesInsightsDriverBreakdownOvertimePaginated = data;
    },
    setChartType(chartType: InsightsChartType) {
      getAppRoot(self).router.replaceQueryParams({ [BaseInsightsQueryParams.CHART_TYPE]: chartType }, true);
    },
  }))
  .actions((self) => ({
    getCompletedRoutesInsights: flow(function* (startDate: string, endDate: string) {
      const data = yield getAppRoot(self)
        .model.queryCompletedRoutesInsights(
          { startDate, endDate },
          InsightsQuery,
          { fetchPolicy: 'no-cache' },
        )
        .currentPromise();
      self.setRouteInsights(data.completedRoutesInsights);
    }),
    getCompletedRoutesInsightsUpdateTimestamp: flow(function* () {
      const data = yield getAppRoot(self)
        .model.queryCompletedRoutesInsightsUpdateTimestamp(
          {},
          { fetchPolicy: 'no-cache' },
        )
        .currentPromise();
      self.setLastUpdatedTimestamp(data.completedRoutesInsightsUpdateTimestamp);
    }),
    getCompletedRoutesInsightsOverTime: flow(function* (
      startDate: string,
      endDate: string,
    ) {
      self.setCompletedRoutesInsightOverTimeQueryParams(startDate, endDate);
      const data = yield getAppRoot(self)
        .model.queryCompletedRoutesInsightsOverTime(
          { offset: 0, limit: 100, startDate, endDate, interval: self.completedRoutesInsightOverTimeQueryParams?.interval ?? InsightsInterval.day },
          InsightsQueryOverTime,
          { fetchPolicy: 'no-cache' },
        )
        .currentPromise();
      self.setCompletedRouteInsightsOverTime(data.completedRoutesInsightsOverTime);
    }),
    getCompletedRoutesInsightsDriverBreakdownOverTime: flow(function* () {
      self.isLoadingDriverBreakdown = true;
      const metricQueryToUse = getMetricQuery(self.selectedMetric);
      const { startDate, endDate } = getInsightsDateRange(getAppRoot(self).router.queryParams);

      const data = yield getAppRoot(self)
        .model.queryCompletedRoutesInsightsDriverBreakdownOverTime(
          {
            offset: 0,
            startDate,
            endDate,
            interval: getDataOverTimeInterval(startDate, endDate),
            direction: InsightsDriverBreakdownSortingDirection.ASC,
            limit: 200, //TODO: We are currently not supporting pagination in the front end and limiting to 200 drivers in the backend.
            //We need to setup monitoring to see when we are getting close to 100 drivers.
            excludeDeletedDrivers: false,
          },
          metricQueryToUse,
          { fetchPolicy: 'cache-and-network' },
        )
        .currentPromise();

      const count = data.completedRoutesInsightsDriverBreakdownOverTime.edges.length;
      const organizationUuid = getAppRoot(self).account.user?.organization.uuid;
      captureDriverCountWarning(count, organizationUuid);

      self.setCompletedRoutesInsightsDriverBreakdownOvertimePaginated(data.completedRoutesInsightsDriverBreakdownOverTime);
      self.isLoadingDriverBreakdown = false;
    }),
  }));

export default Insights;

export interface IUIStore extends Instance<typeof Insights> { }
